import { useActorMember } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { differenceInYears } from 'date-fns'
import { useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { P, Span } from '@/@bothrs-ui/web/components/typography/Body'
import { globalDefinitions } from '@/defined-translations'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { DocumentSignState } from '../../components/document-sign-state/DocumentSignState'
import { DocumentLayout } from '../../components/layout/DocumentLayout'
import { SignableDocumentReviewCard } from '../../components/signable-document-review-card/SignableDocumentReviewCard'
import { SignableDocumentStartSignButton } from '../../components/signable-document-sign-button/SignableDocumentStartSignButton'
import { useSignableDocuments } from '../../hooks/useSignableDocuments'
import { PdfPreview } from '../pdf-preview/PdfPreview'

import type { SignableDocument, SignableDocumentActivity } from '../../types/documents.types'
import type { PatientViewDocumentFlow } from '../../types/patient-documents.types'

type Props = {
  document: PatientViewDocumentFlow
  onAttachmentOpen?: () => void
}

const SignableDocumentDetail = ({ document, onAttachmentOpen }: Props) => {
  // State
  // ------------------------------------------------------------------------- /
  const [selectedDocumentActivity, setSelectedDocumentActivity] = useState<SignableDocumentActivity>()

  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const actorMember = useActorMember()

  const {
    getSignedDocumentActivityPreview: {
      execute: getSignedDocumentActivityPreview,
      value: signedDocumentActivityPreview,
      isLoading: isSignedDocumentActivityPreviewLoading,
      reset: resetSignedDocumentActivityPreview,
    },
  } = useSignableDocuments()

  const actorIsLegalAge = useMemo(() => {
    if (!actorMember?.birthDate) return false
    const date = new Date(actorMember?.birthDate)
    return differenceInYears(new Date(), date) >= 18
  }, [actorMember?.birthDate])

  // Template variables
  // ------------------------------------------------------------------------- /

  // find the activity with the lowest order number, of type 'Message'
  const messageActivity = useMemo(() => {
    if (document.documentFlow?.activities) {
      const firstActivity = document.documentFlow.activities.sort(
        (activityA, activityB) => (activityA.definition?.order ?? 0) - (activityB.definition?.order ?? 0),
      )[0]
      if (firstActivity?.definition?.type === 'Message') {
        return firstActivity
      }
    }
    return null
  }, [document.documentFlow?.activities])

  const signedDocumentActivities =
    (document.documentFlow?.activities?.filter(
      (activity) => (activity as unknown as SignableDocumentActivity).documentId,
    ) as unknown as SignableDocumentActivity[]) ?? []

  const isSigned = (document.documentFlow as unknown as SignableDocument | undefined)?.status === 'signed'

  // Functions
  // ------------------------------------------------------------------------- /
  const openDocument = (activity: SignableDocumentActivity) => {
    onAttachmentOpen?.()
    setSelectedDocumentActivity(activity)
    getSignedDocumentActivityPreview(activity.id)
  }

  const closeDocument = () => {
    if (signedDocumentActivityPreview) {
      URL.revokeObjectURL(signedDocumentActivityPreview)
    }
    setSelectedDocumentActivity(undefined)
    resetSignedDocumentActivityPreview()
  }

  // Render Methods
  // ------------------------------------------------------------------------- /

  if (!document.documentFlow) {
    return null
  }

  const renderSignState = () => {
    if (isSigned) {
      return <DocumentSignState signed date={new Date(document.documentFlow?.updatedAt ?? '')} />
    }
    if (document.expiresAt) {
      return <DocumentSignState date={new Date(document.expiresAt)} />
    }
    return (
      <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Dark}>
        {i18n._(globalDefinitions.task.no_expiry, {
          date: formatLocale(document.date, 'dd/MM/yyyy'),
        })}
      </Span>
    )
  }

  return (
    <>
      <DocumentLayout>
        <DocumentLayout.Header document={document} signState={renderSignState()} />
        <DocumentLayout.Content>
          {messageActivity ? (
            <StMessage type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
              {messageActivity.definition?.message}
            </StMessage>
          ) : null}

          {isSigned ? (
            <FlexColumn gap={theme.UI.Spacing.Space4}>
              <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest} style={{ fontWeight: 'bold' }}>
                {t({
                  id: 'signing.document.signed',
                  message: 'Ondertekende documenten',
                })}
              </Span>
              {signedDocumentActivities?.map((activity: SignableDocumentActivity) => (
                <SignableDocumentReviewCard
                  key={activity.id}
                  title={activity?.definition?.display}
                  icon={['fal', 'file']}
                  description={t({
                    id: 'documents.detail.signed.view-document',
                    message: 'Bekijk dit document',
                  })}
                  reviewed={activity?.status === 'reviewed'}
                  onClick={() => {
                    openDocument(activity)
                  }}
                />
              ))}
            </FlexColumn>
          ) : null}
        </DocumentLayout.Content>

        <DocumentLayout.Footer>
          {isSigned ? null : (
            <SignableDocumentStartSignButton id={document.documentFlow?.id} actorIsLegalAge={actorIsLegalAge} />
          )}
        </DocumentLayout.Footer>
      </DocumentLayout>

      {selectedDocumentActivity ? (
        <PdfPreview
          downloadEnabled
          preview={signedDocumentActivityPreview}
          filename={selectedDocumentActivity?.definition?.display ?? ''}
          loading={isSignedDocumentActivityPreviewLoading}
          onClose={closeDocument}
        />
      ) : null}
    </>
  )
}

export { SignableDocumentDetail }

const StMessage = styled(P)`
  white-space: pre-wrap;
`
